import { clamp, cleanTimelineReferences, createClip, createTrack, creatorId, findClip, fitFades, moveClips, splitClips, updateClip } from './creatorTimeline';

export const TRACK_HEIGHTS = { compact: 60, normal: 88, large: 132 };
export const clipCount = project => project.tracks.reduce((sum, track) => sum + track.clips.length, 0);
export const compatibleTrack = (track, asset) => Boolean(track && asset && !track.locked && (
  track.type === asset.kind || (track.type === 'overlay' && ['image', 'video'].includes(asset.kind))
  || (track.type === 'audio' && asset.has_audio)));

export function addTrack(project, type) {
  if (!['video', 'audio', 'overlay', 'text', 'image'].includes(type) || project.tracks.length >= 16) return project;
  const name = { video: 'Video', audio: 'Audio', overlay: 'Overlay', text: 'Texto', image: 'Imagen' }[type];
  return { ...project, tracks: [...project.tracks, { ...createTrack(type, name), height: 'normal' }] };
}
export function removeTrack(project, id) {
  if (!project.tracks.some(track => track.id === id && !track.locked)) return project;
  return cleanTimelineReferences({ ...project, tracks: project.tracks.filter(track => track.id !== id) });
}
export function duplicateTrack(project, id) {
  const track = project.tracks.find(item => item.id === id);
  if (!track || track.locked || project.tracks.length >= 16 || clipCount(project) + track.clips.length > 80) return project;
  const ids = new Map(track.clips.map(clip => [clip.id, creatorId()]));
  const copy = { ...track, id: creatorId(), name: `${track.name.slice(0, 112)} (copia)`, solo: false,
    clips: track.clips.map(clip => ({ ...clip, id: ids.get(clip.id), transition_in: clip.transition_in
      ? { ...clip.transition_in, from_clip_id: ids.get(clip.transition_in.from_clip_id) } : null })) };
  const index = project.tracks.indexOf(track);
  return cleanTimelineReferences({ ...project, tracks: [...project.tracks.slice(0, index + 1), copy, ...project.tracks.slice(index + 1)] });
}
export function reorderTrackTo(project, id, targetId) {
  const from = project.tracks.findIndex(track => track.id === id);
  const to = project.tracks.findIndex(track => track.id === targetId);
  if (from < 0 || to < 0 || from === to || project.tracks[from].locked || project.tracks[to].locked) return project;
  const tracks = [...project.tracks];
  tracks.splice(to, 0, tracks.splice(from, 1)[0]);
  return { ...project, tracks };
}
export function insertAssets(project, assets, time, trackId, range, mode = 'add') {
  if (!Array.isArray(assets) || !assets.length || !Number.isFinite(time)) return project;
  let next = project;
  let position = Math.max(0, time);
  let targetId = trackId;
  for (const asset of assets) {
    if (!next.assets.some(item => item.id === asset.id) || clipCount(next) >= 80) break;
    let target = next.tracks.find(item => item.id === targetId);
    if (target && !compatibleTrack(target, asset)) {
      if (trackId) return project;
      target = undefined;
    }
    if (!target) {
      target = next.tracks.find(item => compatibleTrack(item, asset) && !item.clips.length);
      if (!target) {
        if (next.tracks.length >= 16) break;
        target = { ...createTrack(asset.kind, asset.filename.slice(0, 120)), height: 'normal' };
        next = { ...next, tracks: [...next.tracks, target] };
      }
    }
    targetId = target.id;
    const clip = createClip(asset, position);
    if (range && asset.kind !== 'image') {
      clip.source_start = clamp(range.source_start || 0, 0, Math.max(0, asset.duration - 1 / project.canvas.fps));
      clip.duration = clamp(range.duration || asset.duration, 1 / project.canvas.fps, asset.duration - clip.source_start);
    }
    if (position + clip.duration > 1800) break;
    if (mode === 'insert') {
      next = splitClips(next, target.clips.map(item => item.id), position);
      next = { ...next, tracks: next.tracks.map(item => item.id === target.id ? { ...item,
        clips: item.clips.map(itemClip => itemClip.start >= position ? { ...itemClip, start: itemClip.start + clip.duration } : itemClip) } : item) };
    } else if (mode === 'overwrite') {
      next = splitClips(next, target.clips.map(item => item.id), position);
      const updated = next.tracks.find(item => item.id === target.id);
      next = splitClips(next, updated.clips.map(item => item.id), position + clip.duration);
      next = { ...next, tracks: next.tracks.map(item => item.id === target.id ? { ...item,
        clips: item.clips.filter(itemClip => itemClip.start + itemClip.duration <= position + 1e-6 || itemClip.start >= position + clip.duration - 1e-6) } : item) };
    }
    next = { ...next, tracks: next.tracks.map(item => item.id === target.id ? { ...item, clips: [...item.clips, clip] } : item) };
    position += clip.duration;
    if (clipCount(next) > 80 || next.tracks.some(item => item.clips.some(value => value.start + value.duration > 1800))) return project;
  }
  return cleanTimelineReferences(next);
}
export function detachAudio(project, id) {
  const found = findClip(project, id);
  if (!found || found.track.locked || found.asset?.kind !== 'video' || !found.asset.has_audio || project.tracks.length >= 16 || clipCount(project) >= 80) return project;
  const audio = { ...found.clip, id: creatorId(), muted: false, transition_in: null, keyframes: [], animation_offset: 0 };
  return cleanTimelineReferences({ ...project, tracks: [...project.tracks.map(track => track.id === found.track.id
    ? { ...track, clips: track.clips.map(clip => clip.id === id ? { ...clip, muted: true } : clip) } : track),
  createTrack('audio', `${found.asset.filename.slice(0, 100)} / audio`, [audio])] });
}
export function replaceClipAsset(project, id, assetId) {
  const found = findClip(project, id);
  const asset = project.assets.find(item => item.id === assetId);
  if (!found || !found.asset || !compatibleTrack(found.track, asset)) return project;
  const duration = asset.kind === 'image' ? found.clip.duration : Math.min(found.clip.duration, asset.duration / found.clip.speed);
  if (duration < 1 / project.canvas.fps) return project;
  const source_start = asset.kind === 'image' ? 0 : Math.min(found.clip.source_start, Math.max(0, asset.duration - duration * found.clip.speed));
  return updateClip(project, id, clip => ({ ...clip, asset_id: asset.id, duration, source_start, speed: asset.kind === 'image' ? 1 : clip.speed, ...fitFades(clip, duration) }));
}
export function moveToTrack(project, ids, delta, targetId, options = {}) {
  const target = project.tracks.find(track => track.id === targetId);
  const selected = ids.map(id => findClip(project, id)).filter(Boolean);
  if (!target || !selected.length || selected.some(item => item.track.locked || (item.clip.text
    ? target.locked || !['text', 'overlay'].includes(target.type) : !compatibleTrack(target, item.asset)))) return project;
  const moved = moveClips(project, ids, delta, options);
  const clips = moved.tracks.flatMap(track => track.clips.filter(clip => ids.includes(clip.id)));
  return cleanTimelineReferences({ ...moved, tracks: moved.tracks.map(track => ({ ...track,
    clips: [...track.clips.filter(clip => !ids.includes(clip.id)), ...(track.id === target.id ? clips : [])] })) });
}
export function rippleDelete(project, ids) {
  return cleanTimelineReferences({ ...project, tracks: project.tracks.map(track => {
    if (track.locked) return track;
    const removed = track.clips.filter(clip => ids.includes(clip.id)).sort((a, b) => a.start - b.start);
    const intervals = [];
    removed.forEach(clip => {
      const last = intervals[intervals.length - 1];
      if (last && clip.start <= last[1]) last[1] = Math.max(last[1], clip.start + clip.duration);
      else intervals.push([clip.start, clip.start + clip.duration]);
    });
    return { ...track, clips: track.clips.filter(clip => !ids.includes(clip.id)).map(clip => ({ ...clip,
      start: Math.max(0, clip.start - intervals.reduce((total, [start, end]) => total + Math.max(0, Math.min(clip.start, end) - start), 0)) })) };
  }) });
}
